/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { IModuleProps, INodeProps, Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import * as Msdyn365 from '@msdyn365-commerce/core';

import {
    IHelp,
    IOrderSummary,
    IPaymentMethods
} from '@msdyn365-commerce-modules/order-management/src/modules/order-confirmation/../../common';
import {
    IGroup,
    IGroupDelivery,
    IGroups
} from '@msdyn365-commerce-modules/order-management/src/modules/order-confirmation/../../common/get-groups';
// import { IOrderInformation } from '@msdyn365-commerce-modules/order-management/src/modules/order-confirmation/../../common/get-order-information';
import { ISalesLine } from '@msdyn365-commerce-modules/order-management/src/modules/order-confirmation/../../common/get-sales-line';
import { IOrderConfirmationData } from '@msdyn365-commerce-modules/order-management';
import { SalesOrder } from '@msdyn365-commerce/retail-proxy';
import { IOrderConfirmationProps } from '../definition-extensions/order-confirmation.ext.props.autogenerated';
import _ from 'lodash';
import { getAttributeByKey } from '../../../utils/cartAttributeManager';
import { chargeLinesLogic, IChargeLinesBreakdownData } from '../../../utils/chargeLinesHelper';

export interface IOrderConfirmationViewProps extends IOrderConfirmationProps<IOrderConfirmationData> {
    moduleProps: IModuleProps;
    salesOrder?: SalesOrder;
    footerProps?: INodeProps;
    alert?: React.ReactNode;
    loading?: React.ReactNode;
    heading: React.ReactNode;
    backToShoppingLink?: React.ReactNode;
    orderInfomation?: IOrderInformation;
    salesLines?: ISalesLine[];
    groups?: IGroups;
    orderSummary?: IOrderSummary;
    payment?: IPaymentMethods;
    help?: IHelp;
    IChargeLinesBreakdownData?: IChargeLinesBreakdownData;
}

export interface EstimatedTaxOverrideValue {
    chargeCode: string;
    description: string;
    amount: number;
}

export interface IOrderInformation {
    orderInformationProps: INodeProps;
    channelName: React.ReactNode;
    channelAddress: React.ReactNode;
    placedBy?: React.ReactNode;
    salesId: React.ReactNode;
    receiptId: React.ReactNode;
    receiptEmail: React.ReactNode;
    createdDate: React.ReactNode;
    count: React.ReactNode;
    amount: React.ReactNode;
    channelReferenceId: React.ReactNode;
    note?: React.ReactNode;
    props: IOrderConfirmationViewProps;
}

interface IPaymentMethodsExt extends IPaymentMethods {
    totalOnAccount?: string;
}

export const OrderConfirmationOrderInfomation: React.FC<IOrderInformation> = ({
    orderInformationProps,
    // receiptEmail,
    createdDate,
    channelReferenceId,
    props
}) => {
    return (
        <Node {...orderInformationProps}>
            {createdDate}
            {channelReferenceId}
            {renderEmailMessage(props)}
        </Node>
    );
};

export const renderEmailMessage = (props: IOrderConfirmationViewProps) => {
    const {
        resources: { receiptMessageWithEmail, receiptMessageNoEmail },
        data: { orderHydration }
    } = props;
    const email = orderHydration.result?.salesOrder.ReceiptEmail;
    let emailMessage = receiptMessageNoEmail;
    if (email) {
        emailMessage = receiptMessageWithEmail.replace('{email}', email);
    }

    // const email = _.get(receiptEmail as object, 'props.children[1].props.children');
    // const messageParts = receiptMessage.split('{email}');
    return (
        <span>
            <Msdyn365.RichTextComponent className='ms-order-confirmation__order-information-receipt-email' text={emailMessage} />
            {/* {messageParts[0]} */}
            {/* <span>{email}</span> */}
            {/* {messageParts[1]} */}
        </span>
    );
};

export const OrderConfirmationSalesLine: React.FC<ISalesLine> = ({ salesLineProps, salesLine, buyAgainButton }) => (
    <Node {...salesLineProps}>{salesLine}</Node>
);

export const OrderConfirmationGroupDelivery: React.FC<IGroupDelivery> = ({
    deliveryProps,
    heading,
    // count,
    // processing,
    address,
    // trackingInfo,
    pickupDateTimeslot
}) => (
    <Node {...deliveryProps}>
        {heading}
        {address}
        {pickupDateTimeslot}
    </Node>
);

export const OrderConfirmationGroup: React.FC<IGroup> = ({ groupProps, delivery, salesLinesProps, salesLines }) => (
    <Node {...groupProps}>
        {delivery && <OrderConfirmationGroupDelivery {...delivery} />}
        {delivery && delivery.shippingItemsToYou}
        {salesLines && (
            <Node {...salesLinesProps}>
                {salesLines.map((salesLine: ISalesLine) => (
                    <OrderConfirmationSalesLine key={salesLine.data.salesLine.LineId} {...salesLine} />
                ))}
            </Node>
        )}
    </Node>
);

export const OrderConfirmationGroups: React.FC<IGroups> = ({ groupsProps, groups }) => (
    <Node {...groupsProps}>
        {groups.map((group: IGroup, index: number) => (
            <OrderConfirmationGroup key={index} {...group} />
        ))}
    </Node>
);

export const otherChargesLines = (props: IOrderConfirmationViewProps) => {
    const [chargeLines, setChargeLines] = React.useState<React.ReactNode[]>([]);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        const fetchOtherChargesLines = async () => {
            const salesOrder = props.data.orderHydration.result?.salesOrder;
            const chargeCodes: IChargeLinesBreakdownData[] = props.context.app.config.chargeLinesBreakdown;
            const attrKey = 'EstimatedTaxOverride';
            const estimatedTaxOverrideAttr = getAttributeByKey((await props.data.orderHydration).salesOrder, attrKey);
            let estimatedTaxOverrideValue: EstimatedTaxOverrideValue | undefined = undefined;
            if (estimatedTaxOverrideAttr) {
                estimatedTaxOverrideValue = JSON.parse(estimatedTaxOverrideAttr?.TextValue!);
            }

            const cartChargeLines = salesOrder?.ChargeLines || [];
            const cartLinesChargeLines = (salesOrder?.SalesLines?.map(item => Array.from(item.ChargeLines || [])) || []).flat();
            const allChargeLines = [...cartChargeLines, ...cartLinesChargeLines];
            let otherChargeTotal = salesOrder?.ChargeAmount || 0;
            let currencyFormatter = props.context.cultureFormatter.formatCurrency;

            const nodes = chargeLinesLogic(
                chargeCodes,
                currencyFormatter,
                allChargeLines,
                renderLineChargeNode,
                otherChargeTotal,
                estimatedTaxOverrideValue
            );

            setChargeLines(nodes);
            setLoading(false);
        };

        fetchOtherChargesLines();
    }, [props.context.actionContext, props.context.cultureFormatter]);

    if (loading) {
        return <div>Loading...</div>;
    }

    return <>{chargeLines}</>;
};

export const renderLineChargeNode = (chargeName: string, chargeAmount: string): React.ReactNode => {
    return (
        <p className='ms-order-confirmation__order-summary-line-shipping'>
            <span className='ms-order-confirmation__order-summary-label'>{chargeName}</span>
            <span className='msc-price ms-order-confirmation__order-summary-price'>
                <span className='msc-price__actual'>{chargeAmount}</span>
            </span>
        </p>
    );
};

export const renderTaxLine = (props: IOrderConfirmationViewProps): React.ReactNode => {
    const taxAmount = props.data.orderHydration.result?.salesOrder.TaxAmount;
    let currencyFormatter = props.context.cultureFormatter.formatCurrency;
    return renderLineChargeNode(props.resources.taxLabel, currencyFormatter(taxAmount!.toString() ?? ''));
};

export const OrderConfirmationOrderSummary: React.FC<IOrderConfirmationViewProps> = props => {
    const orderSummary = props.orderSummary;

    return (
        <Node {...orderSummary!.orderSummaryProps}>
            {orderSummary?.heading}
            {orderSummary?.subtotal}
            {props.config.displayShippingline && orderSummary?.shipping}
            {otherChargesLines(props)}
            {props.config.displayTaxNote ? (
                <p className='ms-order-confirmation__order-summary-line-shipping'>
                    {!!props.config.taxChargeNote ? props.config.taxChargeNote : props.resources.taxAmountNote}
                </p>
            ) : (
                renderTaxLine(props)
            )}

            {orderSummary?.totalAmount}
            {orderSummary?.earnedPoints}
        </Node>
    );
};

export const OrderConfirmationPayment: React.FC<IPaymentMethodsExt> = ({ paymentMethodsProps, title, methods, totalOnAccount }) => (
    <Node {...paymentMethodsProps}>
        {title}
        {totalOnAccount ? <p>On Account: {totalOnAccount}</p> : null}
        {methods}
    </Node>
);

export const OrderConfirmationHelp: React.FC<IHelp> = ({ helpProps, needHelpLabel, helpLineNumberLabel, contactNumber }) => (
    <Node {...helpProps}>
        {needHelpLabel}
        {helpLineNumberLabel}
        {contactNumber}
    </Node>
);

const OrderConfirmationView: React.FC<IOrderConfirmationViewProps> = props => {
    const {
        moduleProps,
        heading,
        backToShoppingLink,
        alert,
        loading,
        orderInfomation,
        orderSummary,
        payment,
        help,
        groups,
        context: {
            cultureFormatter: { formatCurrency }
        }
    } = props;

    const tenderLines = props.data.orderHydration.result?.salesOrder?.TenderLines;

    let totalOnAccount = 0;
    if (tenderLines) {
        totalOnAccount = tenderLines.map(tl => tl.AmountInCompanyCurrency || 0).reduce((num, total) => num + total, 0);
    }

    return (
        <Module {...moduleProps}>
            {heading}
            {alert}
            {loading}
            {orderInfomation && <OrderConfirmationOrderInfomation {...orderInfomation} props={props} />}
            {backToShoppingLink}
            {groups && <OrderConfirmationGroups {...groups} />}
            {payment && (
                <OrderConfirmationPayment {...payment} totalOnAccount={totalOnAccount > 0 ? formatCurrency(totalOnAccount) : undefined} />
            )}
            {orderSummary && <OrderConfirmationOrderSummary {...props} />}
            {help && <OrderConfirmationHelp {...help} />}
        </Module>
    );
};

export default OrderConfirmationView;
